<!-- OPG CoPilot orchestrator content (CR-20260710-001) — lives inside the host's dx-popup.
     Layout: sketch frames 02 (Calls/Puts questionnaire), 06 (wide Calls & Puts), 03/07 (post-run).
     All three tab bodies stay MOUNTED (form state survives tab switches — family idiom); only the
     active one is visible. The dx-popup titlebar owns the close affordance — no ✕ in this content. -->
<div class="copilot" data-element="copilot-window" *ngIf="this.contentVisible">
  <dx-scroll-view height="100%" [showScrollbar]="'onHover'" [useNative]="false">
    <div class="copilot-content" #scrollContent>

      <!-- Header row (WI-10/A6): Calls | Puts | Calls & Puts — full-width segmented tab bar
           (owner review 2026-07-10; disabled while a run is in flight) — plus the refresh
           button AFTER the tabs (the HedgeMatrix CoPilot's header pattern, duplicated): one
           click resets the WHOLE dialog to a fresh conversation. Deliberately NOT disabled
           during a run — clicking it drops the run silently (donor parity).
           WI-15: the button goes YELLOW whenever the grid has drifted from what the CoPilot
           captured — look only; the click that clears it is the same whole-dialog reset. -->
      <!-- Override Indicators (CR-20260722-002) — ABOVE the tab bar, per the owner's sketch.
           Single-side tabs render ONE fieldset; Calls & Puts renders TWO side by side (D11), in
           which IV and HS bind the same properties so the two can never disagree (D5).
           ALWAYS VISIBLE, never collapsed (owner ruling 2026-07-22 — the master toggle was removed
           as redundant with `edited`: an untouched date never reaches the wire). With no date
           resolved yet — a half-picked range — the block still renders, showing '—' with its
           inputs disabled, rather than appearing and disappearing as the pickers are filled.
           D20: the values shown are THIS TAB's overrides, falling back to the shared market
           reading for the date. A tab is an independent request, so an edit made here is invisible
           on the other tabs — while the readings underneath are common to all of them. -->
      <div class="override-block">
        <ng-container *ngIf="this.activeTab !== 'callsAndPuts'">
          <ets-opg-override-indicators
              [value]="this.currentOverride"
              [side]="this.activeTab === 'puts' ? 'puts' : 'calls'"
              [changedKeys]="this.currentOverrideChanged"
              [dateLabel]="this.overrideDateLabel"
              [sourceDateLabel]="this.overrideSourceDateLabel"
              [showArrows]="this.showOverrideArrows"
              [canPrev]="this.canPrevOverrideDate"
              [canNext]="this.canNextOverrideDate"
              [disabled]="this.overrideInputsDisabled"
              [rangeOverridden]="this.rangeOverridden"
              (valueChange)="this.onOverrideChanged($event)"
              (prevDate)="this.onOverridePrev()"
              (nextDate)="this.onOverrideNext()"
              (clearDate)="this.onOverrideClear()"></ets-opg-override-indicators>
        </ng-container>

        <div class="override-duo" *ngIf="this.activeTab === 'callsAndPuts'">
          <ets-opg-override-indicators
              [value]="this.currentOverride"
              [side]="'calls'"
              [sideLabel]="'Calls'"
              [changedKeys]="this.currentOverrideChanged"
              [dateLabel]="this.overrideDateLabel"
              [sourceDateLabel]="this.overrideSourceDateLabel"
              [showArrows]="this.showOverrideArrows"
              [canPrev]="this.canPrevOverrideDate"
              [canNext]="this.canNextOverrideDate"
              [disabled]="this.overrideInputsDisabled"
              [rangeOverridden]="this.rangeOverridden"
              (valueChange)="this.onOverrideChanged($event)"
              (prevDate)="this.onOverridePrev()"
              (nextDate)="this.onOverrideNext()"
              (clearDate)="this.onOverrideClear()"></ets-opg-override-indicators>
          <ets-opg-override-indicators
              [value]="this.currentOverride"
              [side]="'puts'"
              [sideLabel]="'Puts'"
              [changedKeys]="this.currentOverrideChanged"
              [dateLabel]="this.overrideDateLabel"
              [sourceDateLabel]="this.overrideSourceDateLabel"
              [showArrows]="this.showOverrideArrows"
              [canPrev]="this.canPrevOverrideDate"
              [canNext]="this.canNextOverrideDate"
              [disabled]="this.overrideInputsDisabled"
              [rangeOverridden]="this.rangeOverridden"
              (valueChange)="this.onOverrideChanged($event)"
              (prevDate)="this.onOverridePrev()"
              (nextDate)="this.onOverrideNext()"
              (clearDate)="this.onOverrideClear()"></ets-opg-override-indicators>
        </div>
      </div>

      <div class="copilot-header">
        <ets-opg-copilot-tab-bar
            [activeTab]="this.activeTab"
            [notLastRunTab]="this.activeTabNotLastRun"
            [disabled]="this.session.isRunInFlight"
            (activeTabChange)="this.onActiveTabChange($event)"></ets-opg-copilot-tab-bar>
        <!-- CR-20260831-004 owner ruling (screencast, defect round 1): the refresh button reports
             UNDERLYING/GRID drift ONLY — changed-since-run parameters have their own per-control
             yellow and must NOT light it. The collapsed FAB is the one union surface. -->
        <dx-button *ngIf="this.showRefreshButton"
                   icon="refresh" hint="New Conversation" data-element="btn-copilot-refresh"
                   [class.copilot-stale]="this.isGridStale"
                   (onClick)="this.onNewConversation()"></dx-button>
      </div>

      <!-- ================= CALLS tab ================= -->
      <div class="tab-body" [class.ets-hidden]="this.activeTab !== 'calls'">

        <!-- Post-Run breadcrumb strip (frame 03); whole bar is click-to-expand. -->
        <ets-opg-copilot-breadcrumb-bar *ngIf="this.questionnaireCollapsed.calls"
            [tabKind]="'calls'"
            [inputs]="this.workingInputs.calls"
            [loadedDateText]="this.loadedDateText"
            [staleLoadedDate]="this.isExpirationStale"
            [changedKeys]="this.paramChanges.calls"
            (expandRequested)="this.onExpandRequested('calls')"></ets-opg-copilot-breadcrumb-bar>

        <!-- Questionnaire kept ALIVE while collapsed (state preserved); only hidden. -->
        <div class="questionnaire" [class.ets-hidden]="this.questionnaireCollapsed.calls">

          <ets-opg-copilot-date-block
              [class.stale]="this.isExpirationStale"
              [value]="this.workingInputs.calls.date"
              [availableExpirations]="this.availableExpirationKeys"
              [loadedDateText]="this.loadedDateText"
              [baseline]="this.dateBaseline('calls')"
              [disabled]="this.session.isRunInFlight"
              (valueChange)="this.onDateChange('calls', $event)"></ets-opg-copilot-date-block>

          <ets-opg-side-questionnaire
              [class.stale]="this.isChosenAtmStale"
              [side]="'calls'"
              [changedKeys]="this.sideParamChanges.calls"
              [inputs]="this.workingInputs.calls.side" [presetApgSource]="this.presetApgSource('calls')"
              [presetUnderlying]="this.presetUnderlying" [chosenAtmValue]="this.chosenAtmValue"
              [strikeLadder]="this.pickerStrikes"
              [targetInstrument]="this.targetInstrument"
              [strikeExpiration]="this.nearestExpiration"
              [collapsible]="false"
              [collapsed]="false"
              [disabled]="this.session.isRunInFlight"
              [expectedRangeAvailable]="this.expectedRangeAvailable"
              [defaultQty]="this.defaultQty"
              (changed)="this.onSideInputsChanged()"></ets-opg-side-questionnaire>

          <!-- Manual collapse back to the breadcrumb summary (no re-run); shown only once this tab
               has a completed run. Mirrors the breadcrumb 'Click to edit' hint (family symmetry). -->
          <div class="collapse-hint-row" *ngIf="this.hasCompletedRun('calls')">
            <span class="collapse-hint" data-element="btn-collapse-questionnaire"
                  (click)="this.onCollapseRequested('calls')">Collapse Settings</span>
          </div>

          <div class="run-bar">
            <dx-button data-element="btn-run-copilot" text="Ask ORION" type="default"
                       [disabled]="!this.canRun" (onClick)="this.onRun()" width="100%"></dx-button>
          </div>
        </div>

        <!-- Pending / empty / failed states + trade cards (sample chip lives inside). -->
        <ets-opg-copilot-results-list
            [tab]="this.tabOf('calls')"
            [root]="this.root"
            [loadedPriceOf]="this.loadedPriceOf"
            [showLoadAll]="this.previewModel === 'compare'"
            (previewToggled)="this.onPreviewToggled($event)"
            (loadAllRequested)="this.onLoadAllRequested($event)"
            (unPreviewAllRequested)="this.onUnPreviewAllRequested($event)"></ets-opg-copilot-results-list>
      </div>

      <!-- ================= PUTS tab ================= -->
      <div class="tab-body" [class.ets-hidden]="this.activeTab !== 'puts'">

        <ets-opg-copilot-breadcrumb-bar *ngIf="this.questionnaireCollapsed.puts"
            [tabKind]="'puts'"
            [inputs]="this.workingInputs.puts"
            [loadedDateText]="this.loadedDateText"
            [staleLoadedDate]="this.isExpirationStale"
            [changedKeys]="this.paramChanges.puts"
            (expandRequested)="this.onExpandRequested('puts')"></ets-opg-copilot-breadcrumb-bar>

        <div class="questionnaire" [class.ets-hidden]="this.questionnaireCollapsed.puts">

          <ets-opg-copilot-date-block
              [class.stale]="this.isExpirationStale"
              [value]="this.workingInputs.puts.date"
              [availableExpirations]="this.availableExpirationKeys"
              [loadedDateText]="this.loadedDateText"
              [baseline]="this.dateBaseline('puts')"
              [disabled]="this.session.isRunInFlight"
              (valueChange)="this.onDateChange('puts', $event)"></ets-opg-copilot-date-block>

          <ets-opg-side-questionnaire
              [class.stale]="this.isChosenAtmStale"
              [side]="'puts'"
              [changedKeys]="this.sideParamChanges.puts"
              [inputs]="this.workingInputs.puts.side" [presetApgSource]="this.presetApgSource('puts')"
              [presetUnderlying]="this.presetUnderlying" [chosenAtmValue]="this.chosenAtmValue"
              [strikeLadder]="this.pickerStrikes"
              [targetInstrument]="this.targetInstrument"
              [strikeExpiration]="this.nearestExpiration"
              [collapsible]="false"
              [collapsed]="false"
              [disabled]="this.session.isRunInFlight"
              [expectedRangeAvailable]="this.expectedRangeAvailable"
              [defaultQty]="this.defaultQty"
              (changed)="this.onSideInputsChanged()"></ets-opg-side-questionnaire>

          <!-- Manual collapse (no re-run); only after a completed run. See Calls tab. -->
          <div class="collapse-hint-row" *ngIf="this.hasCompletedRun('puts')">
            <span class="collapse-hint" data-element="btn-collapse-questionnaire"
                  (click)="this.onCollapseRequested('puts')">Collapse Settings</span>
          </div>

          <div class="run-bar">
            <dx-button data-element="btn-run-copilot" text="Ask ORION" type="default"
                       [disabled]="!this.canRun" (onClick)="this.onRun()" width="100%"></dx-button>
          </div>
        </div>

        <ets-opg-copilot-results-list
            [tab]="this.tabOf('puts')"
            [root]="this.root"
            [loadedPriceOf]="this.loadedPriceOf"
            [showLoadAll]="this.previewModel === 'compare'"
            (previewToggled)="this.onPreviewToggled($event)"
            (loadAllRequested)="this.onLoadAllRequested($event)"
            (unPreviewAllRequested)="this.onUnPreviewAllRequested($event)"></ets-opg-copilot-results-list>
      </div>

      <!-- ================= CALLS & PUTS tab (wide, frame 06) ================= -->
      <div class="tab-body" [class.ets-hidden]="this.activeTab !== 'callsAndPuts'">

        <ets-opg-copilot-breadcrumb-bar *ngIf="this.questionnaireCollapsed.callsAndPuts"
            [tabKind]="'callsAndPuts'"
            [inputs]="this.workingInputs.callsAndPuts"
            [loadedDateText]="this.loadedDateText"
            [staleLoadedDate]="this.isExpirationStale"
            [changedKeys]="this.paramChanges.callsAndPuts"
            (expandRequested)="this.onExpandRequested('callsAndPuts')"></ets-opg-copilot-breadcrumb-bar>

        <div class="questionnaire" [class.ets-hidden]="this.questionnaireCollapsed.callsAndPuts">

          <!-- The date appears ONCE and binds BOTH sides (the panel's one-base-date rule, §5). -->
          <ets-opg-copilot-date-block
              [class.stale]="this.isExpirationStale"
              [value]="this.workingInputs.callsAndPuts.date"
              [availableExpirations]="this.availableExpirationKeys"
              [loadedDateText]="this.loadedDateText"
              [baseline]="this.dateBaseline('callsAndPuts')"
              [disabled]="this.session.isRunInFlight"
              (valueChange)="this.onDateChange('callsAndPuts', $event)"></ets-opg-copilot-date-block>

          <!-- Calls parameters LEFT, puts RIGHT — side by side, never stacked. Either side
               collapses to a click-to-expand rail while working the other (visual only —
               a collapsed side still gates the Run). The min-height keeps the section at the
               height it had when the SECOND side is collapsed (WI-12) — two bare rails would
               otherwise shrink it; null while any side is expanded = natural sizing. -->
          <div class="duo" #duoSection
               [style.minHeight.px]="this.duoLockedHeightPx"
               [class.calls-collapsed]="this.sideCollapsed.calls"
               [class.puts-collapsed]="this.sideCollapsed.puts">

            <ets-opg-side-questionnaire
                [class.stale]="this.isChosenAtmStale"
                [side]="'calls'"
                [changedKeys]="this.sideParamChanges.duoCalls"
                [inputs]="this.workingInputs.callsAndPuts.calls" [presetApgSource]="this.presetApgSource('calls')"
                [presetUnderlying]="this.presetUnderlying" [chosenAtmValue]="this.chosenAtmValue"
                [strikeLadder]="this.pickerStrikes"
                [targetInstrument]="this.targetInstrument"
                [strikeExpiration]="this.nearestExpiration"
                [collapsible]="true"
                [collapsed]="this.sideCollapsed.calls"
                [disabled]="this.session.isRunInFlight"
                [expectedRangeAvailable]="this.expectedRangeAvailable"
                [defaultQty]="this.defaultQty"
                (collapsedChange)="this.onSideCollapsedChange('calls', $event)"
                (changed)="this.onSideInputsChanged()"></ets-opg-side-questionnaire>

            <ets-opg-side-questionnaire
                [class.stale]="this.isChosenAtmStale"
                [side]="'puts'"
                [changedKeys]="this.sideParamChanges.duoPuts"
                [inputs]="this.workingInputs.callsAndPuts.puts" [presetApgSource]="this.presetApgSource('puts')"
                [presetUnderlying]="this.presetUnderlying" [chosenAtmValue]="this.chosenAtmValue"
                [strikeLadder]="this.pickerStrikes"
                [targetInstrument]="this.targetInstrument"
                [strikeExpiration]="this.nearestExpiration"
                [collapsible]="true"
                [collapsed]="this.sideCollapsed.puts"
                [disabled]="this.session.isRunInFlight"
                [expectedRangeAvailable]="this.expectedRangeAvailable"
                [defaultQty]="this.defaultQty"
                (collapsedChange)="this.onSideCollapsedChange('puts', $event)"
                (changed)="this.onSideInputsChanged()"></ets-opg-side-questionnaire>
          </div>

          <!-- Manual collapse (no re-run); only after a completed run. See Calls tab. -->
          <div class="collapse-hint-row" *ngIf="this.hasCompletedRun('callsAndPuts')">
            <span class="collapse-hint" data-element="btn-collapse-questionnaire"
                  (click)="this.onCollapseRequested('callsAndPuts')">Collapse Settings</span>
          </div>

          <!-- Still ONE Run — both sides go in a single request (§5). -->
          <div class="run-bar">
            <dx-button data-element="btn-run-copilot" text="Ask ORION" type="default"
                       [disabled]="!this.canRun" (onClick)="this.onRun()" width="100%"></dx-button>
          </div>
        </div>

        <!-- Call cards LEFT column, put cards RIGHT — never mixed in one line (§6/§10);
             the results list owns the two-column layout (frame 07). WI-16/A8: each results
             column collapses on its OWN state (resultsSideCollapsed), INDEPENDENT of the
             questionnaire's sideCollapsed above — the old §6 lockstep is repealed (owner
             2026-07-14). Collapse control = the results column header; expand = its rail. -->
        <ets-opg-copilot-results-list
            [tab]="this.tabOf('callsAndPuts')"
            [root]="this.root"
            [loadedPriceOf]="this.loadedPriceOf"
            [showLoadAll]="this.previewModel === 'compare'"
            [callsCollapsed]="this.resultsSideCollapsed.calls"
            [putsCollapsed]="this.resultsSideCollapsed.puts"
            (callsCollapsedChange)="this.onResultsSideCollapsedChange('calls', $event)"
            (putsCollapsedChange)="this.onResultsSideCollapsedChange('puts', $event)"
            (previewToggled)="this.onPreviewToggled($event)"
            (loadAllRequested)="this.onLoadAllRequested($event)"
            (unPreviewAllRequested)="this.onUnPreviewAllRequested($event)"></ets-opg-copilot-results-list>
      </div>

    </div>
  </dx-scroll-view>
</div>

<!-- Warned clear-on-run confirm (WI-5). OUTSIDE *ngIf="contentVisible" so it stays mounted
     (and its @ViewChild stays resolved) across fresh-conversation toggles. show() is driven
     from onRun() when a Run would wipe an already-loaded panel. -->
<ets-generic-confirmation-dialog></ets-generic-confirmation-dialog>
